import React, { useState, useEffect, useContext } from 'react';
import { CheckboxProps } from 'antd';

import ModalTask from 'components/BorrowerTask/components/Organisms/Modals/ModalTask/ModalTask';
import CheckboxBasic from 'components/Atoms/Checkboxs/CheckboxBasic/CheckboxBasic';
import leafPng from 'assets/images/leaf.png';

import { LoanContext } from '../LoanDetails';
import { useCreateLoanDoc } from 'services/loan/mutations';
import { LoanDocState } from 'gql/graphql';
import { useGetLoanDocByLoan } from 'services/loan/querys';
import { useGetTasksByLoan } from 'services/task/querys';

// Imports de los componentes de formulario específicos
import GenericDocumentForm from './Generic/GenericDocument';
import InitialDocsForm from './InitialDocs/InitialDocs';
import AppraisalForm from './TaskLinkedDocs/Appraisal';
import TitleForm from './TaskLinkedDocs/Title';
import InsuranceForm from './TaskLinkedDocs/Insurance';
import ClosingDocsForm from './closingDocs/ClosingDoc';


import './CreateLoanDoc.scss';
import CreditReportForm from './CreditAndBackground/CreditReport';
import BackgroundCheckForm from './CreditAndBackground/BackgroundCheck';
import { checkDisabled, hiddenCheckboxDisabled } from './constants';

interface Props {
  selectLoanDoc?: any;
  loanDocs?: any[];
  setSelectLoanDoc: Function;
  handlerOpenTask: Function;
}

const CreateLoanDoc = ({ selectLoanDoc, setSelectLoanDoc, loanDocs, handlerOpenTask }: Props) => {
  const loanSelected: any = useContext(LoanContext);
  const { execute: createLoanDoc } = useCreateLoanDoc();
  const { reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({ 
    variables: { loanId: loanSelected?._id }, 
    pause: true 
  });
  const { reexecute: getTasksByLoan } = useGetTasksByLoan({
    variables: { loanId: loanSelected?._id },
    pause: true
  });

  const [isModalTaskVisible, setModalTaskVisible] = useState(false);

  // Detectar tipo de documento
  const isCreditReportDocument = selectLoanDoc?.label?.includes('Credit Report');
  const isBackgroundCheckDocument = selectLoanDoc?.label?.includes('Background Check');
  const isCreditReportOrBackgroundCheck = isCreditReportDocument || isBackgroundCheckDocument;

  // Encontrar el documento existente
  const isLoanDoc = loanDocs?.find(item => 
    item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
  );

  const isLoanDocNotExist = selectLoanDoc === undefined;

  useEffect(() => {
    if (selectLoanDoc) {
      setModalTaskVisible(true);
    } else {
      setModalTaskVisible(false);
    }
  }, [selectLoanDoc]);

  const handlerOpenModal = (action: any) => {
    action(true);
    setSelectLoanDoc();
  };

  const onHideDoc: CheckboxProps['onChange'] = async (e) => {
    await createLoanDoc({
      _id: isLoanDoc?._id,
      key: selectLoanDoc?.key,
      name: selectLoanDoc?.label,
      loan: loanSelected._id,
      hidden: e.target.checked,
      state: isLoanDoc?.state || LoanDocState.Pending
    });

    getLoanDocByLoan({ requestPolicy: 'network-only' });
    getTasksByLoan({ requestPolicy: 'network-only' });
  };

  const onFormSuccess = () => {
    setModalTaskVisible(false);
    setSelectLoanDoc();
  };

  const renderDocumentForm = () => {
    // Si no hay documento seleccionado, mostrar formulario genérico
    if (!selectLoanDoc)
      return <GenericDocumentForm selectLoanDoc={selectLoanDoc} loanDocs={loanDocs} onSuccess={onFormSuccess} />;

    if (isCreditReportDocument)
      return <CreditReportForm loanDocs={loanDocs} selectLoanDoc={selectLoanDoc} />;

    if (isBackgroundCheckDocument)
      return <BackgroundCheckForm loanDocs={loanDocs} selectLoanDoc={selectLoanDoc} />;

    switch (selectLoanDoc.key) {
      case 'term_sheet':
      case 'pre_approval':
        return (
          <InitialDocsForm 
            selectLoanDoc={selectLoanDoc} 
            loanDocs={loanDocs}
          />
        );
      case 'appraisal':
        return (
          <AppraisalForm 
            selectLoanDoc={selectLoanDoc} 
            loanDocs={loanDocs}
          />
        );
      case 'title':
        return (
          <TitleForm 
            selectLoanDoc={selectLoanDoc} 
            loanDocs={loanDocs}
          />
        );
      case 'insurance':
        return (
          <InsuranceForm 
            selectLoanDoc={selectLoanDoc} 
            loanDocs={loanDocs}
            handlerOpenTask={handlerOpenTask}
          />
        );
      case 'closing_docs':
        return (
          <ClosingDocsForm 
            selectLoanDoc={selectLoanDoc} 
            loanDocs={loanDocs}
          />
        );
      default:
        return <GenericDocumentForm selectLoanDoc={selectLoanDoc} loanDocs={loanDocs} onSuccess={onFormSuccess} />;
    }
  };

  return (
    <ModalTask
      visible={isModalTaskVisible}
      handlerSetVisibile={setModalTaskVisible}
      renderButtonTrigger={(openModal: any) => (
        <div className='action-button' onClick={() => handlerOpenModal(openModal)} style={{ cursor: 'pointer' }}>
          Upload loan document
        </div>
      )}
      header={{
        title: <span>{loanSelected.owner.name + ' ' + loanSelected.owner.lastname} | {loanSelected.name}</span>,
        actionLeft: (<img src={leafPng} alt="icon" className="paperPng" />)
      }}
      animationDirection="onRight"
      dots={false}
      stepsTotal={1}
      currentStep={1}
      setStep={() => { }}
      footer={<></>}
    >
      <div className='CreateLoanDoc'>
        <section>
          {!selectLoanDoc &&
            <h2 className="h2">
              Upload a loan document for{' '}
              <span>{loanSelected.owner.name + ' ' + loanSelected.owner.lastname} {loanSelected.type}</span>
              {' '}loan in {loanSelected.name}
            </h2>
          }
          {selectLoanDoc &&
            <h2 className="h2">
              What is the status of {selectLoanDoc?.label} {selectLoanDoc?.label === 'title' || selectLoanDoc?.label === 'insurance' ? 'Documents' : ''}?
            </h2>
          }
          {renderDocumentForm()}
          <div className='hide-doc'>
            <CheckboxBasic
              defaultChecked={checkDisabled.includes(selectLoanDoc?.key) || isLoanDoc?.hidden}
              onChange={onHideDoc}
              label='Hide this doc from the borrower'
              disabled={
                hiddenCheckboxDisabled.includes(selectLoanDoc?.key) ||
                checkDisabled.includes(selectLoanDoc?.key) ||
                isCreditReportOrBackgroundCheck ||
                isLoanDocNotExist
              }
            />
          </div>
        </section>
      </div>
    </ModalTask>
  );
};

export default CreateLoanDoc;